import { useState, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { useApi, asArray } from '../hooks/useApi'
import { ContentHeader } from '../components/layout/ContentHeader'
import KeywordTable from '../components/rankings/KeywordTable'

interface RankingKeywordRow {
  id: number
  keyword: string
  device: string
  location: string
  position: number | null
  url: string | null
  delta: number | null
  volume: number | null
  manualPosition: number | null
  checkedAt: string | null
}

interface RankingsData {
  keywords: RankingKeywordRow[]
  visibility: number
  distribution: any
  pages: any[]
  competitors: any[]
  quota: any
  domain: string
  searchSettings: any
}

const FILTER_CONFIG: Record<string, { label: string; description: string; filter: (kw: RankingKeywordRow) => boolean }> = {
  'entered-top10': {
    label: 'Entrées dans le Top 10',
    description: 'Mots-clés qui sont entrés ou ont progressé dans le top 10',
    filter: kw => kw.position !== null && kw.position <= 10 && kw.delta !== null && kw.delta > 0,
  },
  'entered-top100': {
    label: 'Entrées dans le Top 100',
    description: 'Mots-clés nouvellement apparus dans les résultats',
    filter: kw => kw.position !== null && kw.delta === null,
  },
  'best': {
    label: 'Meilleurs mots-clés',
    description: 'Mots-clés avec les meilleures positions',
    filter: kw => kw.position !== null,
  },
  'improved': {
    label: 'Mots-clés améliorés',
    description: 'Mots-clés ayant gagné des positions',
    filter: kw => kw.delta !== null && kw.delta > 0,
  },
  'declined': {
    label: 'Mots-clés en recul',
    description: 'Mots-clés ayant perdu des positions',
    filter: kw => kw.delta !== null && kw.delta < 0,
  },
}

export default function RankingDetailed() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeFilter = searchParams.get('filter') || 'all'

  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const {
    data: rankingsData,
    loading,
    error,
    refetch,
  } = useApi<RankingsData>(`/api/keywords/clients/${projectId}`)

  if (error === 'Unauthorized' || error === '401') {
    navigate('/login')
    return null
  }

  const allKeywords = asArray(rankingsData?.keywords)
  const config = FILTER_CONFIG[activeFilter]

  const keywords = useMemo(() => {
    if (!config) return allKeywords
    return allKeywords.filter(config.filter)
  }, [allKeywords, activeFilter])

  async function handleAddKeyword(keyword: string) {
    setAdding(true)
    setAddError(null)
    try {
      const res = await fetch(`/api/keywords/clients/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ keyword }),
      })
      const json = await res.json()
      if (!res.ok) setAddError(json.error || 'Erreur')
      else refetch()
    } catch {
      setAddError('Erreur réseau')
    } finally {
      setAdding(false)
    }
  }

  async function handleDeleteKeyword(id: number) {
    await fetch(`/api/keywords/${id}`, { method: 'DELETE', credentials: 'include' })
    refetch()
  }

  return (
    <div className="space-y-4">
      <ContentHeader
        breadcrumb={[
          { label: 'Dashboard', to: `/p/${projectId}` },
          { label: 'Classements', to: `/p/${projectId}/rankings` },
          { label: 'Vue détaillée' },
        ]}
        title={config?.label || 'Tous les mots-clés'}
        subtitle={config?.description || `${keywords.length} mots-clés`}
      />

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setSearchParams({})}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            activeFilter === 'all'
              ? 'bg-orion-primary text-white'
              : 'bg-[#F4F5FA] text-[#555A73] hover:text-[#1B2559]'
          }`}
        >
          Tous {allKeywords.length}
        </button>
        {Object.entries(FILTER_CONFIG).map(([key, cfg]) => {
          const count = allKeywords.filter(cfg.filter).length
          return (
            <button
              key={key}
              onClick={() => setSearchParams({ filter: key })}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activeFilter === key
                  ? 'bg-orion-primary text-white'
                  : 'bg-[#F4F5FA] text-[#555A73] hover:text-[#1B2559]'
              }`}
            >
              {cfg.label} {count}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="w-6 h-6 animate-spin text-[#8B8FA7]" strokeWidth={1.25} />
        </div>
      ) : (
        <KeywordTable
          keywords={keywords}
          onAdd={handleAddKeyword}
          onDelete={handleDeleteKeyword}
          adding={adding}
          addError={addError}
          projectId={projectId!}
          maxKeywords={50}
        />
      )}
    </div>
  )
}
